"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import "./processSteps.css";

const STEPS = [
  {
    num: "01",
    title: "Discover Your Business",
    body: "We understand your goals, audience, competitors, and market to create a clear strategy that drives sustainable business growth and success.",
  },
  {
    num: "02",
    title: "Research & Strategy",
    body: "AIGIO researches your market, evaluates competitors, and prepares a focused strategy designed for your business growth.",
  },
  {
    num: "03",
    title: "Build Your Brand",
    body: "Generate your complete brand identity with AI, then review and approve every recommendation before confidently finalizing your brand strategy.",
  },
  {
    num: "04",
    title: "Launch With Confidence",
    body: "Get launch-ready branding, marketing plans, and content aligned with your approved strategy for consistent growth.",
  },
];

export default function ProcessSteps() {
  const sectionRef = useRef<HTMLElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([null, null, null, null]);

  const [progress, setProgress] = useState(0);
  const [paths, setPaths] = useState<string[]>(["", "", ""]);
  const [svgBox, setSvgBox] = useState({ w: 0, h: 0 });

  const isTablet = (w: number) => w <= 1024 && w > 400;
  const [stacked, setStacked] = useState(false);

  // Synchronize state with window dimensions safely on mount
  useEffect(() => {
    setStacked(isTablet(window.innerWidth));
    const onResize = () => setStacked(isTablet(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const TOTAL_PHASES = 7;

  const measureAndBuild = useCallback(() => {
    if (!stickyRef.current || !gridRef.current) return;

    const gridRect = gridRef.current.getBoundingClientRect();

    // Avoid setting 0 dimensions if layout isn't ready
    if (gridRect.width === 0 || gridRect.height === 0) return;

    const edges = cardRefs.current.map((ref) => {
      if (!ref) return null;
      const r = ref.getBoundingClientRect();
      return {
        top: r.top - gridRect.top,
        bottom: r.bottom - gridRect.top,
        left: r.left - gridRect.left,
        right: r.right - gridRect.left,
        cx: r.left - gridRect.left + r.width / 2,
        cy: r.top - gridRect.top + r.height / 2,
      };
    });

    // If any cards aren't mounted/rendered yet, skip this calculation pass safely
    if (edges.some((e) => !e)) return;

    const [e0, e1, e2, e3] = edges as any;
    const currentWidth = window.innerWidth;

    if (currentWidth <= 400) {
      // MOBILE CONNECTORS
      const p0 = `
        M${e0.right - 3},${e0.bottom - 95}
        C${e0.right + 100},${e0.bottom + 118}
         ${e1.left + 140},${e1.top - 80}
         ${e1.left + 120},${e1.top}
      `;

      const p1 = `
        M${e1.left + 3},${e1.bottom - 55}
        C${e1.left - 90},${e1.bottom + 108}
         ${e2.right - 160},${e2.top - 78}
         ${e2.right - 115},${e2.top}
      `;

      const p2 = `
        M${e2.right - 3},${e2.bottom - 75}
        C${e2.right + 110},${e2.bottom + 108}
         ${e3.left + 10},${e3.top - 100}
         ${e3.left + 120},${e3.top}
      `;

      setSvgBox({ w: gridRect.width, h: gridRect.height });
      setPaths([p0, p1, p2]);
    } else if (currentWidth <= 1024) {
      // TABLET CONNECTORS (Static vertical flow paths)
      const p0 = `M${e0.cx},${e0.bottom} L${e1.cx},${e1.top}`;
      const p1 = `M${e1.cx},${e1.bottom} L${e2.cx},${e2.top}`;
      const p2 = `M${e2.cx},${e2.bottom} L${e3.cx},${e3.top}`;

      setSvgBox({ w: gridRect.width, h: gridRect.height });
      setPaths([p0, p1, p2]);
    } else {
      // DESKTOP CONNECTORS
      const p0 = `
  M ${e0.cx}, ${e0.bottom}
  C ${e0.cx-100}, ${e0.bottom + 150}
    ${e1.left - 120}, ${e1.cy + 0}
    ${e1.left}, ${e1.cy}
`;

const p1 = `
  M ${e1.cx}, ${e1.top}
  C ${e1.cx - 100}, ${e1.top - 150}
    ${e2.left - 120}, ${e2.cy}
    ${e2.left}, ${e2.cy}
`;

// This replicates your exact intended shape: starts on the right side of Card 3,
// curves out smoothly, and plunges cleanly downwards into the top of Card 4.
// This recreates your exact white curve: exits the bottom of Card 3, 
// swoops down, and enters cleanly into the left-middle edge of Card 4.
// This recreates your exact white curve: exits the bottom of Card 3, 
// swoops down, and enters cleanly into the left-middle edge of Card 4.
const p2 = `
  M ${e2.cx}, ${e2.bottom}
  C ${e2.cx-100}, ${e2.bottom + 150}
    ${e3.left - 120}, ${e3.cy + 0}
    ${e3.left}, ${e3.cy}
`;
      setSvgBox({ w: gridRect.width, h: gridRect.height });
      setPaths([p0, p1, p2]);
    }
  }, []);

  // Use a ResizeObserver to trigger paths whenever layout stabilizes/resizes
  useEffect(() => {
    const target = gridRef.current;
    if (!target) return;

    const observer = new ResizeObserver(() => {
      measureAndBuild();
    });

    observer.observe(target);
    // Fallback invocation
    measureAndBuild();

    return () => {
      observer.disconnect();
    };
  }, [measureAndBuild]);

  // Handle Scroll Progress
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;

      const { top, height } = el.getBoundingClientRect();
      const scrollable = height - window.innerHeight;

      if (scrollable <= 0) return;

      setProgress(Math.min(Math.max(-top / scrollable, 0), 1));
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const phase = progress * TOTAL_PHASES;

  const cardVisible = [
  stacked || phase >= 0.1,
  stacked || phase >= 3,
  stacked || phase >= 5,
  stacked || phase >= 7,

  ];

  const lineProgress = [
  stacked
    ? 1
    : phase >= 1.5
    ? Math.min(phase - 1.5, 1)
    : 0,

  stacked
    ? 1
    : phase >= 3.5
    ? Math.min(phase - 3.5, 1)
    : 0,

  stacked
    ? 1
    : phase >= 5.5
    ? Math.min(phase - 5.5, 1)
    : 0,
  ];

  return (
    <section ref={sectionRef} className="process-section">
      <div ref={stickyRef} className="process-sticky">
        <div className="process-container">
          <div className="process-header">
            <p className="process-label">PROCESS</p>
            <div className="process-title">
              From idea to
              <br />
              launch in four guided stages.
            </div>
          </div>

          <div ref={gridRef} className="process-grid-wrapper">
            {svgBox.w > 0 && (
              <svg
                className="process-svg"
                viewBox={`0 0 ${svgBox.w} ${svgBox.h}`}
              >
                {paths.map((d, i) => {
                  if (!d) return null; // Prevent rendering incomplete paths
                  return (
                    <AnimatedPath
                      key={i}
                      d={d}
                      progress={lineProgress[i]}
                    />
                  );
                })}
              </svg>
            )}

            <div className="steps-grid">
              {STEPS.map((step, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  className={`step-position step-${i + 1}`}
                >
                  <StepCard step={step} visible={cardVisible[i]} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({
  step,
  visible,
}: {
  step: { num: string; title: string; body: string };
  visible: boolean;
}) {
  return (
    <div className="step-card-shadow">
      <div className={`step-card ${visible ? "visible" : ""}`}>
        <div className="step-number">{step.num}</div>
        <div className="step-card-title">{step.title}</div>
        <div className="step-card-body">{step.body}</div>
      </div>
    </div>
  );
}

function AnimatedPath({ d, progress }: { d: string; progress: number }) {
  const pathRef = useRef<SVGPathElement>(null);

  const [length, setLength] = useState(0);
  const [arrowPos, setArrowPos] = useState({ x: 0, y: 0 });
  const [arrowAngle, setArrowAngle] = useState(0);

  useEffect(() => {
    if (!pathRef.current || !d) return;

    try {
      const totalLength = pathRef.current.getTotalLength();
      if (!isFinite(totalLength) || totalLength <= 0) {
        setLength(0);
        return;
      }
      setLength(totalLength);
    } catch {
      setLength(0);
    }
  }, [d]);

  useEffect(() => {
    if (!pathRef.current || length <= 0 || !isFinite(progress)) return;

    try {
      const currentLength = Math.max(0, Math.min(length * progress, length));
      const point = pathRef.current.getPointAtLength(currentLength);

      setArrowPos({ x: point.x, y: point.y });

      const back = Math.max(0, currentLength - 1);
      const prev = pathRef.current.getPointAtLength(back);
      const angle = (Math.atan2(point.y - prev.y, point.x - prev.x) * 180) / Math.PI;

      setArrowAngle(angle);
    } catch {
      // fallback safe boundary catch
    }
  }, [progress, length]);

  if (!d) return null;

  const drawn = length * progress;

  return (
    <>
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="#2c2f45"
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeDasharray={length || 1}
        strokeDashoffset={length - drawn}
      />
      {progress > 0 && length > 0 && (
<polygon
  points="2,0 -13,-7 -13,7"
  fill="#2c2f45"
  transform={`translate(${arrowPos.x}, ${arrowPos.y}) rotate(${arrowAngle})`}
/>
      )}
    </>
  );
}